import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Filter } from "lucide-react";

import { FilterField, NativeSelect } from "./widgets";
import { PERIOD_PILLS, pillRange } from "./utils";

/**
 * Period pills + filters card. Manages filter state through props (parent
 * owns `filters` and reacts to its changes via `useEffect`).
 */
export default function AnalyticsFilters({
  filters,
  setFilters,
  options,
  loading,
  activePill,
  setActivePill,
  onClear,
}) {
  function applyPill(key) {
    setActivePill(key);
    const { from, to } = pillRange(key);
    setFilters({ ...filters, date_from: from, date_to: to });
  }

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {PERIOD_PILLS.map((p) => (
          <button
            key={p.key}
            onClick={() => applyPill(p.key)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              activePill === p.key
                ? "bg-blue-700 text-white border-blue-700"
                : "bg-white text-slate-700 border-slate-200 hover:border-blue-400"
            }`}
            data-testid={`pill-${p.key}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <Filter className="h-4 w-4" /> Filtros
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3">
            <FilterField label="De">
              <Input type="date" lang="pt-BR" value={filters.date_from}
                onChange={(e) => { setFilters({ ...filters, date_from: e.target.value }); setActivePill(""); }}
                data-testid="filter-from" />
            </FilterField>
            <FilterField label="Até">
              <Input type="date" lang="pt-BR" value={filters.date_to}
                onChange={(e) => { setFilters({ ...filters, date_to: e.target.value }); setActivePill(""); }}
                data-testid="filter-to" />
            </FilterField>
            <FilterField label="Cirurgião">
              <NativeSelect value={filters.surgeon_id}
                onChange={(v) => setFilters({ ...filters, surgeon_id: v })}
                options={options.surgeons.map((s) => ({ value: s.id, label: s.full_name }))}
                testid="filter-surgeon" />
            </FilterField>
            <FilterField label="Auxiliar">
              <NativeSelect value={filters.assistant_id}
                onChange={(v) => setFilters({ ...filters, assistant_id: v })}
                options={options.surgeons.map((s) => ({ value: s.id, label: s.full_name }))}
                testid="filter-assistant" />
            </FilterField>
            <FilterField label="Anestesista">
              <NativeSelect value={filters.anesthesiologist_id}
                onChange={(v) => setFilters({ ...filters, anesthesiologist_id: v })}
                options={options.anesths.map((s) => ({ value: s.id, label: s.full_name }))}
                testid="filter-anesth" />
            </FilterField>
            <FilterField label="Instrumentador">
              <NativeSelect value={filters.instrument_technician_id}
                onChange={(v) => setFilters({ ...filters, instrument_technician_id: v })}
                options={options.techs.map((s) => ({ value: s.id, label: s.full_name }))}
                testid="filter-tech" />
            </FilterField>
            <FilterField label="Procedimento">
              <NativeSelect value={filters.procedure_id}
                onChange={(v) => setFilters({ ...filters, procedure_id: v })}
                options={options.procedures.map((p) => ({ value: p.id, label: p.name }))}
                testid="filter-procedure" />
            </FilterField>
            <FilterField label="Hospital">
              <NativeSelect value={filters.hospital_id}
                onChange={(v) => setFilters({ ...filters, hospital_id: v })}
                options={options.hospitals.map((h) => ({ value: h.id, label: h.name }))}
                testid="filter-hospital" />
            </FilterField>
            <FilterField label="Turno">
              <NativeSelect value={filters.period}
                onChange={(v) => setFilters({ ...filters, period: v })}
                options={[
                  { value: "manha", label: "Manhã" },
                  { value: "tarde", label: "Tarde" },
                  { value: "noite", label: "Noite" },
                ]}
                testid="filter-shift" />
            </FilterField>
          </div>
          <div className="flex flex-wrap gap-6 items-center pt-2 border-t border-slate-100">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Switch checked={filters.only_confirmed}
                onCheckedChange={(v) => setFilters({ ...filters, only_confirmed: v })}
                data-testid="toggle-only-confirmed" />
              Apenas confirmadas (executadas)
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Switch checked={filters.include_suspended}
                onCheckedChange={(v) => setFilters({ ...filters, include_suspended: v })}
                data-testid="toggle-include-suspended" />
              Incluir suspensas
            </label>
            {loading && <span className="text-xs text-slate-500">Recalculando…</span>}
          </div>
        </CardContent>
      </Card>
    </>
  );
}
